/**
 * Session module — single source of truth for client-side auth state.
 *
 * Owns three things:
 *   1. The in-memory access token + its expiry (cleared on hard reload).
 *   2. `forceSignOut()` — the SINGLE funnel through which every code
 *      path leaves the authenticated state. apiClient (on 401-after-
 *      refresh-failed), AuthProvider (on status='unauthenticated'),
 *      and the user-initiated logout button all go through here.
 *   3. The refresh-cookie is OWNED BY THE BACKEND. This module never
 *      touches `hh_refresh` directly — the backend's Set-Cookie /
 *      delete_cookie response headers do that. JS literally cannot
 *      read it (HttpOnly).
 *
 * No React imports here. Imported by `apiClient`, `useAuth` hook, and
 * any UI surface that needs to read the current token.
 */

let accessToken: string | null = null;
let tokenExpiresAt: number | null = null;
let sessionOwnerId: string | null = null;

/** Used by durable writes so a restored draft cannot cross an account change. */
export function getSessionOwnerId(): string | null { return sessionOwnerId; }

/**
 * Return the current access token IF it's still within its TTL minus
 * a 30-second grace window. Returns null if absent or expiring soon —
 * the caller (apiClient / useAuth) is expected to trigger a refresh.
 */
export function getAccessToken(): string | null {
  if (accessToken && tokenExpiresAt) {
    const now = Date.now() / 1000;
    if (now >= tokenExpiresAt - 30) return null;
  }
  return accessToken;
}

export function setAccessToken(token: string, expiresIn: number, ownerId?: string): void {
  accessToken = token;
  tokenExpiresAt = Date.now() / 1000 + expiresIn;
  if (ownerId !== undefined) sessionOwnerId = ownerId;
}

export function clearAccessToken(): void {
  accessToken = null;
  tokenExpiresAt = null;
  sessionOwnerId = null;
}

export function isAccessTokenValid(): boolean {
  return getAccessToken() !== null;
}

// ─── forceSignOut — the single sign-out funnel ───────────────────────

/**
 * Closed set of reasons sign-out can be triggered. Every call site
 * must name one — visible in devtools console so the cause of a
 * logout is debuggable without copy-paste guessing.
 */
export type SignOutReason =
  | "user-logout"      // user clicked the logout button
  | "session-expired"  // refresh token expired / revoked at backend
  | "token-revoked"    // 401-after-refresh-failed (replay detected, etc)
  | "no-cookie";       // AuthProvider sees status=unauthenticated post-hydrate

/**
 * The single sign-out funnel. Every redirect-to-login goes through here.
 *
 * What it does:
 *   1. Clear the in-memory access token.
 *   2. Hard-navigate to /login with the current path stored in `?from=`
 *      so the next successful login can return the user where they were.
 *
 * Why hard navigation (window.location.href) instead of router.replace:
 *   A sign-out is an auth boundary — we want a fresh page load so no
 *   stale React state survives. Soft navigation would keep mounted
 *   components alive with cached user data.
 *
 * Safe to call from non-browser contexts (no-op when window is absent).
 * Safe to call twice in a row (the second call sees we're already at
 * /login and short-circuits).
 */
export function forceSignOut(reason: SignOutReason): void {
  clearAccessToken();
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  const from = window.location.pathname + window.location.search;
  const url = `/login?from=${encodeURIComponent(from)}`;
  console.info(`[auth] forceSignOut(${reason})`);
  window.location.href = url;
}
